import React, { useState } from 'react';
import {
  ArrowRight,
  Sparkles,
  Save,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Plus,
  Trash2
} from 'lucide-react';
import { marketplaces } from '../utils/mockData';
import { MappingService } from '../services/allServices';

const loadMappings = async () => {
  const data = await MappingService.getMappings();
  setMappings(data.mappings);
};

const CategoryMappingRow = ({ mapping, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const marketplace = marketplaces.find(m => m.id === mapping.marketplaceId);

  return (
    <div className="bg-dark-800/30 border border-dark-700/30 rounded-lg p-4 hover:border-dark-600/50 transition-all">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{marketplace?.logo}</span>
          <div>
            <h3 className="text-dark-100 font-medium">{marketplace?.name}</h3>
            <p className="text-dark-400 text-sm">Categoria interna: {mapping.internalCategory}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {mapping.confidence && (
            <div className="flex items-center gap-1 px-3 py-1 bg-primary-500/10 text-primary-400 rounded-full text-sm">
              <Sparkles className="w-4 h-4" />
              {Math.round(mapping.confidence * 100)}% confiança
            </div>
          )}
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="p-2 hover:bg-dark-700 rounded-lg transition-colors text-dark-300 hover:text-dark-100"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={() => onDelete(mapping.id)}
            className="p-2 hover:bg-red-500/10 rounded-lg transition-colors text-dark-300 hover:text-red-400"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {/* Internal Category */}
        <div className="p-4 bg-dark-900/50 border border-dark-700/50 rounded-lg">
          <p className="text-dark-500 text-xs mb-2">Categoria Interna</p>
          <p className="text-dark-100 font-medium">{mapping.internalCategory}</p>
          <p className="text-dark-400 text-sm mt-1">ID: {mapping.internalCategoryId}</p>
        </div>

        {/* Marketplace Category */}
        <div className="p-4 bg-primary-500/5 border border-primary-500/20 rounded-lg">
          <p className="text-dark-500 text-xs mb-2">Categoria {marketplace?.name}</p>
          {isEditing ? (
            <input
              type="text"
              defaultValue={mapping.marketplaceCategory}
              className="w-full bg-dark-800 border border-dark-600 rounded px-2 py-1 text-dark-100 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              onBlur={(e) => {
                onUpdate(mapping.id, { marketplaceCategory: e.target.value });
                setIsEditing(false);
              }}
            />
          ) : (
            <>
              <p className="text-dark-100 font-medium">{mapping.marketplaceCategory}</p>
              <p className="text-dark-400 text-sm mt-1">ID: {mapping.marketplaceCategoryId}</p>
            </>
          )}
        </div>
      </div>

      {/* Attribute Mappings */}
      {mapping.attributes && mapping.attributes.length > 0 && (
        <div className="mt-4 space-y-2">
          <p className="text-sm font-medium text-dark-300">Mapeamento de Atributos</p>
          <div className="grid grid-cols-2 gap-2">
            {mapping.attributes.map((attr, idx) => (
              <div key={idx} className="flex items-center gap-2 p-2 bg-dark-900/30 rounded text-sm">
                <span className="text-dark-400">{attr.internalName}</span>
                <ArrowRight className="w-3 h-3 text-dark-600" />
                <span className="text-dark-100">{attr.marketplaceName}</span>
                {attr.required && (
                  <span className="ml-auto text-xs text-red-400">*</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const NewMappingForm = ({ onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    internalCategory: '',
    internalCategoryId: '',
    marketplaceId: '',
    marketplaceCategory: '',
    marketplaceCategoryId: '',
  });
  const [aiSuggestion, setAiSuggestion] = useState(null);

  const handleAiSuggest = () => {
    // Simular sugestão de IA
    setAiSuggestion({
      category: 'Vestuário > Feminino > Vestidos',
      categoryId: 'ML-1234',
      confidence: 0.92,
    });
  };

  return (
    <div className="bg-dark-800/50 border-2 border-primary-500/30 rounded-xl p-6 animate-slide-up">
      <h3 className="text-xl font-semibold text-dark-100 mb-6">Novo Mapeamento de Categoria</h3>

      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Internal Category */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-2">
              Categoria Interna
            </label>
            <input
              type="text"
              value={formData.internalCategory}
              onChange={(e) => setFormData({ ...formData, internalCategory: e.target.value })}
              placeholder="ex: Fashion > Women > Dresses"
              className="w-full px-4 py-3 bg-dark-900/50 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-2">
              ID Interno
            </label>
            <input
              type="text"
              value={formData.internalCategoryId}
              onChange={(e) => setFormData({ ...formData, internalCategoryId: e.target.value })}
              placeholder="ex: CAT-001"
              className="w-full px-4 py-3 bg-dark-900/50 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        </div>

        {/* Marketplace Category */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-2">
              Marketplace
            </label>
            <select
              value={formData.marketplaceId}
              onChange={(e) => setFormData({ ...formData, marketplaceId: e.target.value })}
              className="w-full px-4 py-3 bg-dark-900/50 border border-dark-700 rounded-lg text-dark-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Selecione...</option>
              {marketplaces.map((mp) => (
                <option key={mp.id} value={mp.id}>{mp.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-2">
              Categoria do Marketplace
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.marketplaceCategory}
                onChange={(e) => setFormData({ ...formData, marketplaceCategory: e.target.value })}
                placeholder="ex: Moda > Feminino > Vestidos"
                className="flex-1 px-4 py-3 bg-dark-900/50 border border-dark-700 rounded-lg text-dark-100 placeholder-dark-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                onClick={handleAiSuggest}
                className="px-4 py-3 bg-gradient-to-r from-primary-500 to-purple-500 hover:from-primary-600 hover:to-purple-600 text-dark-100 rounded-lg font-medium transition-all flex items-center gap-2 shadow-lg shadow-primary-500/20"
              >
                <Sparkles className="w-4 h-4" />
                IA
              </button>
            </div>
            {aiSuggestion && (
              <div className="mt-2 p-3 bg-primary-500/10 border border-primary-500/20 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-primary-300">Sugestão da IA:</p>
                    <p className="text-dark-100 font-medium">{aiSuggestion.category}</p>
                  </div>
                  <div className="text-primary-400 text-sm">
                    {Math.round(aiSuggestion.confidence * 100)}% confiança
                  </div>
                </div>
                <button
                  onClick={() => setFormData({
                    ...formData,
                    marketplaceCategory: aiSuggestion.category,
                    marketplaceCategoryId: aiSuggestion.categoryId,
                  })}
                  className="mt-2 text-sm text-primary-400 hover:text-primary-300 transition-colors"
                >
                  Aplicar sugestão →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-dark-700 hover:bg-dark-600 text-dark-100 rounded-lg font-medium transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={() => onSave(formData)}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg font-medium transition-colors shadow-lg shadow-primary-500/20 flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          Salvar Mapeamento
        </button>
      </div>
    </div>
  );
};

export default function MappingTool() {
  const [showNewForm, setShowNewForm] = useState(false);
  const [mappings, setMappings] = useState([
    {
      id: '1',
      internalCategory: 'Fashion > Women > Dresses',
      internalCategoryId: 'CAT-001',
      marketplaceId: 'ml',
      marketplaceCategory: 'Moda e Acessórios > Roupas > Vestidos',
      marketplaceCategoryId: 'ML-1234',
      confidence: 0.95,
      attributes: [
        { internalName: 'Cor', marketplaceName: 'COLOR', marketplaceId: 'ML-ATT-001', required: true },
        { internalName: 'Tamanho', marketplaceName: 'SIZE', marketplaceId: 'ML-ATT-002', required: true },
        { internalName: 'Material', marketplaceName: 'MATERIAL', marketplaceId: 'ML-ATT-003', required: false },
      ],
      createdAt: '2025-01-15',
    },
    {
      id: '2',
      internalCategory: 'Fashion > Women > Dresses',
      internalCategoryId: 'CAT-001',
      marketplaceId: 'amazon',
      marketplaceCategory: 'Clothing, Shoes & Jewelry > Women > Dresses',
      marketplaceCategoryId: 'AMZ-5678',
      confidence: 0.88,
      attributes: [
        { internalName: 'Cor', marketplaceName: 'Color', marketplaceId: 'AMZ-ATT-001', required: true },
        { internalName: 'Tamanho', marketplaceName: 'Size', marketplaceId: 'AMZ-ATT-002', required: true },
      ],
      createdAt: '2025-01-20',
    },
  ]);

  const handleSaveMapping = (newMapping) => {
    setMappings([...mappings, {
      ...newMapping,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    }]);
    setShowNewForm(false);
  };

  const handleUpdateMapping = (id, updates) => {
    setMappings(mappings.map(m => m.id === id ? { ...m, ...updates } : m));
  };

  const handleDeleteMapping = (id) => {
    setMappings(mappings.filter(m => m.id !== id));
  };

  return (
    <div className="p-8 space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-display font-bold text-dark-100 mb-2">
            Gestão de Mapeamento
          </h1>
          <p className="text-dark-400">
            Conecte suas categorias internas às taxonomias dos marketplaces
          </p>
        </div>
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg font-medium transition-colors shadow-lg shadow-primary-500/20"
        >
          <Plus className="w-5 h-5" />
          Novo Mapeamento
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-dark-700/50">
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle className="w-8 h-8 text-emerald-400" />
            <div>
              <p className="text-2xl font-bold text-dark-100">{mappings.length}</p>
              <p className="text-dark-400 text-sm">Mapeamentos Ativos</p>
            </div>
          </div>
        </div>
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-dark-700/50">
          <div className="flex items-center gap-3 mb-3">
            <Sparkles className="w-8 h-8 text-primary-400" />
            <div>
              <p className="text-2xl font-bold text-dark-100">
                {Math.round(mappings.reduce((acc, m) => acc + (m.confidence || 0), 0) / mappings.length * 100)}%
              </p>
              <p className="text-dark-400 text-sm">Confiança Média IA</p>
            </div>
          </div>
        </div>
        <div className="bg-dark-800/50 backdrop-blur-sm rounded-xl p-6 border border-dark-700/50">
          <div className="flex items-center gap-3 mb-3">
            <AlertCircle className="w-8 h-8 text-amber-400" />
            <div>
              <p className="text-2xl font-bold text-dark-100">0</p>
              <p className="text-dark-400 text-sm">Pendentes de Revisão</p>
            </div>
          </div>
        </div>
      </div>

      {/* New Mapping Form */}
      {showNewForm && (
        <NewMappingForm
          onSave={handleSaveMapping}
          onCancel={() => setShowNewForm(false)}
        />
      )}

      {/* Mappings List */}
      <div className="space-y-4">
        {mappings.map((mapping) => (
          <CategoryMappingRow
            key={mapping.id}
            mapping={mapping}
            onUpdate={handleUpdateMapping}
            onDelete={handleDeleteMapping}
          />
        ))}
      </div>

      {/* Empty State */}
      {mappings.length === 0 && !showNewForm && (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4">
            <ArrowRight className="w-8 h-8 text-dark-500" />
          </div>
          <h3 className="text-xl font-semibold text-dark-100 mb-2">Nenhum mapeamento criado</h3>
          <p className="text-dark-400 mb-6">Comece criando seu primeiro mapeamento de categoria</p>
          <button
            onClick={() => setShowNewForm(true)}
            className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-dark-100 rounded-lg font-medium transition-colors shadow-lg shadow-primary-500/20"
          >
            Criar Mapeamento
          </button>
        </div>
      )}
    </div>
  );
}
